import type { PlaybookLocale } from '../types'

export const enPlaybook: PlaybookLocale = {
  hero: {
    tag: 'Playbook · Operational guide',
    title: 'The patterns that actually ship',
    subtitle: '11 sections · all extracted from real customer engagements',
    leadParagraph:
      'The Method page explains why ontology works. This page is the operational counterpart — concrete patterns and anti-patterns for shipping ontology + agent in production. Pick a section, skim it, come back when you hit that obstacle.',
    backToMethodLabel: '← Back to Method',
  },
  sections: [
    {
      id: 'where-to-start',
      number: '01',
      title: 'Where to start',
      lead: 'Pick the first scenario by these four criteria — skip if more than two fail.',
      blocks: [
        {
          kind: 'table2col',
          headers: ['Criterion', 'Meaning'],
          rows: [
            ['Clear rules', 'Business boundaries and decision criteria can be made explicit. Fuzzy domains (creative work, sentiment) are not first stops.'],
            ['Data available', 'Most key data already lives in ERP / MES / CRM / docs — it just isn\'t connected.'],
            ['Closed loop measurable', 'Quantifiable metrics (response time, error rate, compliance) can produce a before/after in 1-2 months.'],
            ['Business owner in the room', 'A specific business lead willing to co-author rules, review outputs, and iterate.'],
          ],
        },
        {
          kind: 'callout',
          tag: 'Anti-patterns',
          title: "Don't start here",
          body: 'A company-wide knowledge base · A generic agent platform with some configuration · "Let\'s switch to a stronger model" · "Finish data governance first, then start". Each of these stalls in demo limbo.',
        },
        {
          kind: 'bullets',
          bullets: [
            'Quality traceability / customer-complaint response — clear chain, sharp cross-dept pain, quantifiable.',
            'Operations analytics / natural-language Q&A — high-frequency, high-value, decision-makers will pay.',
            'Customer service — structured data, naturally connects to business systems.',
          ],
        },
      ],
    },
    {
      id: 'how-to-model',
      number: '02',
      title: 'How to model',
      lead: 'Three things have to come together. Skipping any one wrecks the others.',
      blocks: [
        {
          kind: 'table3col',
          headers: ['Element', 'What it captures', 'Example'],
          rows: [
            ['Semantic (Knowledge)', 'Entities, relations, events, attributes', 'Customer, order, equipment, batch, work-step'],
            ['Action', 'Operations the agent is allowed to perform — role / attribute / scope', 'Create ticket, update alert state, place purchase order'],
            ['Logic', 'Executable business logic — query / workflow / autonomous agent', '"Material anomaly → trace work-orders → flag finished goods"'],
          ],
        },
        {
          kind: 'numberedBullets',
          items: [
            {
              title: 'Standardise concepts',
              body: 'The most common failure: one concept, N names. List every alias across systems (material_id / raw_id / supplier-batch / inbound-code) and map them all to one entity.',
            },
            {
              title: 'Structure relations',
              body: 'Every relation the human mind handles as "obviously related" must be made explicit — raw→finished (input-of), finished→work-order (produced-by), work-step→equipment (uses), supplier→risk-rating (graded), complaint→batch→work-step (traces-to).',
            },
            {
              title: 'Codify rules',
              body: 'Convert "what the senior engineer knows" into computable rules: "credit ≥ $100M → VP committee", "humidity > X% → workstep prone to defect", "cost overrun 10% → human approval".',
            },
          ],
        },
      ],
    },
    {
      id: 'choose-engine',
      number: '03',
      title: 'Workflow vs Agentic',
      lead: 'One engine isn\'t enough — and one prompt definitely isn\'t.',
      blocks: [
        {
          kind: 'table2col',
          headers: ['Task profile', 'Use'],
          rows: [
            ['Path known, audit needed, process controlled', 'Workflow'],
            ['Each step\'s conditions known / config-driven', 'Workflow'],
            ['Many tweaks but flow stays', 'Workflow'],
            ['Long task, uncertain, needs runtime planning', 'Agentic'],
            ['Multi-step reasoning, goal-driven', 'Agentic'],
            ['Exploratory, root-cause analysis, what-if', 'Agentic'],
          ],
        },
        {
          kind: 'callout',
          tag: 'In practice',
          title: 'They mix inside a single scenario',
          body: 'Quality traceability: the trace itself (raw → workstep → finished) is Workflow because the path is fixed. The root-cause analysis is Agentic because the path is unknown. The improvement plan is Agentic. The rectification work-order dispatch is Workflow.',
        },
      ],
    },
    {
      id: 'behavior-control',
      number: '04',
      title: 'Behaviour control',
      lead: 'Business hard constraints become agent hard constraints — not "please don\'t hallucinate" but "this rule literally cannot be violated".',
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            'Define constraints **in the ontology**, not in prompts. "Purchase > $1M needs VP approval" is a hard rule, encoded once.',
            'Agent-generated plans must **pass ontology validation**. Violations trigger re-planning.',
            'Validation is **visible** — business users see why a plan was rejected.',
          ],
        },
        {
          kind: 'callout',
          tag: 'Conflict resolution',
          title: 'What if the agent disagrees with the ontology?',
          body: 'In-scope conflict → hard constraint overrides, agent re-plans. Out-of-scope domain → agent\'s flexible reasoning wins, but result is labelled "unverified".',
        },
        {
          kind: 'numberedBullets',
          items: [
            {
              title: 'Standard operations',
              body: 'BOM entry, routine process adjustments — agent acts autonomously. Audit log captured.',
            },
            {
              title: 'Above-threshold decisions',
              body: 'Cost overrun > 10%, contract value > VP-threshold — human approval triggered. System pre-fills a decision-support report.',
            },
            {
              title: 'Override right',
              body: 'Humans always retain the right to reverse an agent decision. Maintains accountability and reasonableness.',
            },
          ],
        },
      ],
    },
    {
      id: 'traceability',
      number: '05',
      title: 'Traceability by construction',
      lead: 'Audit-ready is not a retrofit — it\'s a property of the architecture.',
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            'Every reasoning step links back to the rule (defined in ontology).',
            'Every data point links back to the source field.',
            'Every action links back to the executing agent / workflow.',
            'Every trigger links back to the time and the user / event that fired it.',
          ],
        },
        {
          kind: 'callout',
          tag: 'Concrete',
          title: 'A quality trace chain',
          body: 'raw-batch P-2104 → inbound inspection → work-orders W-781, W-782 → equipment R-031, R-033 → work-steps (weld, assemble) → finished batches F-901..F-985 → outbound + customers C-12, C-15, C-44. If any link breaks, system tells you which link, why, and how to repair it.',
        },
      ],
    },
    {
      id: 'cold-start',
      number: '06',
      title: 'Cold start — from weeks to hours',
      lead: "Don't wait for the perfect ontology to start using it.",
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            'Auto-induction for structurally-obvious tasks.',
            'Confidence-driven routing: low-confidence tasks go to a human review queue.',
            'Human feedback continually improves the auto-induction.',
            'A Domain Ontology Library (~3,600 concepts, ~8,400 relations) provides the starter scaffold.',
          ],
        },
        {
          kind: 'table2col',
          headers: ['Scope', 'Duration'],
          rows: [
            ['Business validation', '1-2 weeks'],
            ['General scope', 'Under 1 month'],
            ['High-complexity scope', '1-6 months'],
          ],
        },
      ],
    },
    {
      id: 'deployment',
      number: '07',
      title: 'Pick deployment by data sensitivity',
      blocks: [
        {
          kind: 'table2col',
          headers: ['Mode', 'Best fit'],
          rows: [
            ['On-prem', 'Finance / government / healthcare / regulated manufacturing — most customers'],
            ['Cloud', 'SMB / low-sensitivity workloads / fast iteration'],
            ['Hybrid', 'Mixed-sensitivity portfolio across business units — recommended path'],
          ],
        },
      ],
    },
    {
      id: 'coherence',
      number: '08',
      title: 'Avoid "more AI, more fragmentation"',
      lead: "Application count goes up; intelligence stays scattered. That's the failure to avoid.",
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            'Anti-pattern: every new scenario starts fresh; each department invents its own logic; new tools land before semantic alignment.',
            'Coherence comes from: one shared domain ontology · reusable Onto-Skills (quality traceability, ticket dispatch as standard primitives) · cognition that sediments back into the ontology.',
          ],
        },
        {
          kind: 'callout',
          tag: 'How to test coherence',
          body: 'Same concept means the same thing across every agent · a new rule changes once (in the ontology) · the same data is structured the same way for every consumer.',
        },
      ],
    },
    {
      id: 'quality',
      number: '09',
      title: "How to test 'AI really understands'",
      lead: "Don't judge by fluent answers — judge by these four axes.",
      blocks: [
        {
          kind: 'table3col',
          headers: ['Axis', 'Looks like working', 'Really participating'],
          rows: [
            ['Answer vs reason', 'Pulls data, calculates, summarises', 'Also answers "why" and "is this anomalous?"'],
            ['API vs flow', 'Connected to interfaces', 'Decisions actually moved from human to system'],
            ['Handle vs sediment', 'One-off response', 'Data feeds back, system gets smarter'],
            ['Execute vs decide', 'Completes the action', 'Offers a judgement path with evidence at decision points'],
          ],
        },
      ],
    },
    {
      id: 'anti-patterns',
      number: '10',
      title: 'Anti-patterns cheat-sheet',
      blocks: [
        {
          kind: 'table2col',
          headers: ['Anti-pattern', 'Do this instead'],
          rows: [
            ['Build the knowledge graph, then think about business', 'Build the scenario ontology — the KG is one layer of it'],
            ['One prompt solves everything', 'Workflow + Agentic dual engine; pick per task'],
            ['Finish data governance first, then start', 'Governance happens through ontology itself'],
            ['Let the agent run autonomously, deal with errors later', 'Encode business constraints; violations roll back'],
            ['Bigger model = better', 'More accurate business semantics = better'],
            ['Connecting to the system = shipped', 'Decisions moved from human to system = shipped'],
            ['Rebuild from scratch every scenario', 'One domain ontology; reuse across scenarios'],
            ['More templates = more universal', 'More templates = more semantic fragmentation'],
          ],
        },
      ],
    },
    {
      id: 'readiness',
      number: '11',
      title: 'Readiness checklist',
      lead: 'Before kicking off, answer these nine. All nine = go. Three or more unanswered = circle back to §01.',
      blocks: [
        {
          kind: 'checklist',
          items: [
            'Can we name the core business objects? (Customers, orders, equipment, …)',
            'Can we draw the relations between them?',
            'Which rules are hard (must follow), which are soft (can be overridden)?',
            'Which actions can the agent perform autonomously, which need human approval?',
            'Is there a business owner committed to defining rules and reviewing outputs?',
            'Can the first scenario produce before/after data in 1-2 months?',
            'What is the data sensitivity? (Decides deployment mode.)',
            'When things go wrong, how granular must the audit trail be?',
            'If this scenario succeeds, where does it extend next?',
          ],
        },
      ],
    },
  ],
}
